import { css, html, LitElement } from 'lit'
import { property, query, state } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
@safeCustomElement('mdc-scroll-container')
export class MdcScrollContainer extends LitElement {
  @property({ type: String }) gap = '1.5rem'

  @query('.mdc-scroll-container') private _container!: HTMLDivElement

  @state() private _canScrollLeft = false
  @state() private _canScrollRight = false

  private _resizeObserver?: ResizeObserver

  static styles = css`
    :host {
      display: block;
    }

    .mdc-scroll-container {
      --_fade-size: 2rem;
      display: flex;
      overflow-x: auto;
      scrollbar-width: none;
    }

    .mdc-scroll-container::-webkit-scrollbar {
      display: none;
    }
  `

  private _updateScrollState() {
    const el = this._container
    if (!el) return
    const threshold = 1
    this._canScrollLeft = el.scrollLeft > threshold
    this._canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - threshold
  }

  private _getMaskStyle(): string {
    const left = this._canScrollLeft
    const right = this._canScrollRight
    if (!left && !right) return ''

    const fadeL = 'linear-gradient(to right, transparent, black var(--_fade-size))'
    const solidM = 'linear-gradient(black, black)'
    const fadeR = 'linear-gradient(to left, transparent, black var(--_fade-size))'

    let mask: string
    if (left && right) {
      mask = `${fadeL}, ${solidM}, ${fadeR}`
      const pos = 'left top, var(--_fade-size) top, right top'
      const size =
        'var(--_fade-size) 100%, calc(100% - var(--_fade-size) * 2) 100%, var(--_fade-size) 100%'
      return `-webkit-mask-image: ${mask}; mask-image: ${mask}; -webkit-mask-position: ${pos}; mask-position: ${pos}; -webkit-mask-size: ${size}; mask-size: ${size}; -webkit-mask-repeat: no-repeat; mask-repeat: no-repeat;`
    } else if (right) {
      mask = fadeR
    } else {
      mask = fadeL
    }

    return `-webkit-mask-image: ${mask}; mask-image: ${mask};`
  }

  firstUpdated() {
    this._resizeObserver = new ResizeObserver(() => this._updateScrollState())
    this._resizeObserver.observe(this._container)
    this._updateScrollState()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this._resizeObserver?.disconnect()
  }

  render() {
    return html`
      <div
        class="mdc-scroll-container"
        style="gap: ${this.gap}; ${this._getMaskStyle()}"
        @scroll=${this._updateScrollState}
      >
        <slot @slotchange=${this._updateScrollState}></slot>
      </div>
    `
  }
}
